/**
 * Visibility Control v2 — shared types, defaults and helpers for scheduling a
 * form's open/close window and the messages shown outside that window.
 *
 * The builder only authors + persists this config on the saved form payload
 * (`formData.form.formSchedule`). The respondent-facing enforcement (comparing
 * "now" to the window and rendering the before/after page or redirect) lives in
 * the separate widget-renderer service.
 */

/** How the form's open boundary is decided. */
export type ScheduleOpenMode = "anytime" | "date";
/** How the form's close boundary is decided. */
export type ScheduleCloseMode = "never" | "date";
/** What a visitor sees while the form is outside the open window. */
export type VisibilityStateMode = "page" | "redirect";
/** The two configurable out-of-window states. */
export type VisibilityStateKey = "before" | "after";

export interface ScheduleBoundary {
  /** `anytime`/`never` = unbounded, `date` = bound to `date` + `time`. */
  mode: ScheduleOpenMode | ScheduleCloseMode;
  /** ISO date `YYYY-MM-DD` (empty when unbounded). */
  date: string;
  /** 24h time `HH:mm` (empty when unbounded). */
  time: string;
}

export interface VisibilityState {
  mode: VisibilityStateMode;
  /** Rich-text HTML shown when `mode === "page"`. */
  html: string;
  /** Full URL visitors are sent to when `mode === "redirect"`. */
  redirectUrl: string;
}

export type ShadowValue = "none" | "soft" | "elevated";

export interface BoxSides {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** A margin side: px value or "auto" (auto centers the card horizontally). */
export type MarginSide = number | "auto";

export interface MarginSides {
  top: MarginSide;
  right: MarginSide;
  bottom: MarginSide;
  left: MarginSide;
}

/** Visual styling applied to the before/after pages (consumed by the renderer). */
export interface ScheduleAppearance {
  cardBackground: string;
  pageBackground: string;
  cornerRadius: number;
  cardWidth: number;
  /** Card min-height in px; 0 = auto. */
  cardHeight: number;
  shadow: ShadowValue;
  padding: BoxSides;
  margin: MarginSides;
  /** Show agency logo/name on the closed-state pages. */
  agencyBranding: boolean;
}

export interface FormSchedule {
  enabled: boolean;
  /** IANA timezone name, e.g. "Asia/Kolkata". Empty falls back to browser TZ. */
  timezone: string;
  open: ScheduleBoundary;
  close: ScheduleBoundary;
  states: Record<VisibilityStateKey, VisibilityState>;
  appearance: ScheduleAppearance;
}

const createVisibilityState = (): VisibilityState => ({
  mode: "page",
  html: "",
  redirectUrl: "",
});

const createAppearance = (): ScheduleAppearance => ({
  cardBackground: "#ffffff",
  pageBackground: "#f9fafb",
  cornerRadius: 14,
  cardWidth: 460,
  cardHeight: 0,
  shadow: "soft",
  padding: { top: 48, right: 40, bottom: 48, left: 40 },
  margin: { top: "auto", right: "auto", bottom: "auto", left: "auto" },
  agencyBranding: false,
});

/** Coerce a persisted margin side to a safe value ("auto" or a non-negative px). */
const normalizeMarginSide = (
  value: unknown,
  fallback: MarginSide
): MarginSide => {
  if (value === "auto") {
    return "auto";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  return fallback;
};

/**
 * Fresh default schedule config. Disabled, unbounded window, empty messages.
 * Used for store defaults, load hydration fallback, and tests.
 */
export const getDefaultFormSchedule = (): FormSchedule => ({
  enabled: false,
  timezone: "",
  open: { mode: "anytime", date: "", time: "" },
  close: { mode: "never", date: "", time: "" },
  states: {
    before: createVisibilityState(),
    after: createVisibilityState(),
  },
  appearance: createAppearance(),
});

/**
 * Merge a persisted (possibly partial/legacy) schedule payload onto the
 * defaults so hydration never yields undefined nested fields.
 */
export const normalizeFormSchedule = (raw: unknown): FormSchedule => {
  const defaults = getDefaultFormSchedule();
  if (!raw || typeof raw !== "object") {
    return defaults;
  }
  const value = raw as Partial<FormSchedule>;
  const appearance: Partial<ScheduleAppearance> = value.appearance ?? {};
  return {
    enabled: value.enabled ?? defaults.enabled,
    timezone: value.timezone ?? defaults.timezone,
    open: { ...defaults.open, ...(value.open ?? {}) },
    close: { ...defaults.close, ...(value.close ?? {}) },
    states: {
      before: { ...defaults.states.before, ...(value.states?.before ?? {}) },
      after: { ...defaults.states.after, ...(value.states?.after ?? {}) },
    },
    appearance: {
      ...defaults.appearance,
      ...appearance,
      padding: {
        ...defaults.appearance.padding,
        ...(appearance.padding ?? {}),
      },
      margin: {
        top: normalizeMarginSide(
          appearance.margin?.top,
          defaults.appearance.margin.top
        ),
        right: normalizeMarginSide(
          appearance.margin?.right,
          defaults.appearance.margin.right
        ),
        bottom: normalizeMarginSide(
          appearance.margin?.bottom,
          defaults.appearance.margin.bottom
        ),
        left: normalizeMarginSide(
          appearance.margin?.left,
          defaults.appearance.margin.left
        ),
      },
    },
  };
};

export interface TimezoneOption {
  label: string;
  value: string;
  // Index signature keeps this assignable to ghl-ui's SelectBaseOption.
  [key: string]: string;
}

/** Small resilient fallback used when the runtime can't enumerate IANA zones. */
const FALLBACK_TIMEZONES: string[] = [
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

const timeZonePart = (zone: string, name: "longOffset" | "long"): string => {
  // Format at "now", not the epoch — offsets and zone names change over time
  // (e.g. Asia/Thimphu was GMT+05:30 in 1970 but is GMT+06:00 today).
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: name,
  }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
};

/**
 * Format a zone as "GMT+05:30 — India Standard Time (Asia/Kolkata)". The IANA
 * zone name is included because distinct zones can share an offset AND a long
 * name (Asia/Kolkata vs Asia/Colombo are both "India Standard Time") — without
 * it they render as indistinguishable duplicate rows, and city names would not
 * be searchable in the filterable select.
 */
const formatOffsetLabel = (zone: string): string => {
  const readable = zone.replace(/_/g, " ");
  try {
    const offset = timeZonePart(zone, "longOffset"); // e.g. "GMT+05:30"
    const longName = timeZonePart(zone, "long"); // e.g. "India Standard Time"
    if (offset && longName && longName !== offset) {
      return `${offset} — ${longName} (${readable})`;
    }
    return offset ? `${offset} — ${readable}` : readable;
  } catch {
    return readable;
  }
};

/**
 * Build the timezone dropdown options from the runtime's IANA zone list,
 * labelled with a GMT offset. Falls back to a curated list when
 * `Intl.supportedValuesOf` is unavailable (older runtimes / jsdom).
 */
export const getTimezoneOptions = (): TimezoneOption[] => {
  const intlWithValues = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  let zones: string[] = FALLBACK_TIMEZONES;
  try {
    if (typeof intlWithValues.supportedValuesOf === "function") {
      const supported = intlWithValues.supportedValuesOf("timeZone");
      if (Array.isArray(supported) && supported.length > 0) {
        // ICU's IANA list omits "UTC", but getBrowserTimezone() can resolve to
        // it — keep it selectable so those users see their current zone.
        zones = supported.includes("UTC") ? supported : [...supported, "UTC"];
      }
    }
  } catch {
    zones = FALLBACK_TIMEZONES;
  }
  return zones.map((zone) => ({ label: formatOffsetLabel(zone), value: zone }));
};

/** Browser's current IANA timezone, used as the default selection. */
export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** Runtime state of the form relative to its open window. */
export type ScheduleState = "before" | "open" | "closed";

export interface ScheduleStatus {
  state: ScheduleState;
  /** ms since epoch for the open boundary, or null when unbounded ("anytime"). */
  openAt: number | null;
  /** ms since epoch for the close boundary, or null when unbounded ("never"). */
  closeAt: number | null;
}

/** Combine a boundary's `date` + `time` into ms since epoch, or null when unset. */
export const getBoundaryMillis = (
  boundary: ScheduleBoundary
): number | null => {
  if (boundary.mode !== "date" || !boundary.date) {
    return null;
  }
  const time = boundary.time || "00:00";
  const millis = new Date(`${boundary.date}T${time}`).getTime();
  return Number.isNaN(millis) ? null : millis;
};

/**
 * Pure computation of which state ("before" / "open" / "closed") a form is in,
 * used to drive the builder's live-status timeline. Wall-clock comparison —
 * authoritative timezone-aware enforcement is the renderer's job.
 */
export const computeScheduleStatus = (
  schedule: FormSchedule,
  now: number = Date.now()
): ScheduleStatus => {
  const openAt = getBoundaryMillis(schedule.open);
  const closeAt = getBoundaryMillis(schedule.close);
  let state: ScheduleState = "open";
  if (openAt !== null && now < openAt) {
    state = "before";
  } else if (closeAt !== null && now >= closeAt) {
    state = "closed";
  }
  return { state, openAt, closeAt };
};

/** Validation error codes for the open/close boundaries. */
export type ScheduleFieldError = "closeBeforeOpen";

export interface ScheduleErrors {
  open: ScheduleFieldError | null;
  close: ScheduleFieldError | null;
}

/**
 * Pure validation of the scheduling window: a dated close boundary must be
 * after the open boundary. Past boundaries are NOT errors — a past open/close
 * simply means the form has opened/closed (the normal lifecycle); picking a
 * past time is prevented by the picker's disabled dates/times instead.
 */
export const getScheduleErrors = (
  open: ScheduleBoundary,
  close: ScheduleBoundary
): ScheduleErrors => {
  const openAt = getBoundaryMillis(open);
  const closeAt = getBoundaryMillis(close);
  const errors: ScheduleErrors = { open: null, close: null };
  if (openAt !== null && closeAt !== null && closeAt <= openAt) {
    errors.close = "closeBeforeOpen";
  }
  return errors;
};

export const hasScheduleErrors = (errors: ScheduleErrors): boolean =>
  Boolean(errors.open || errors.close);

/**
 * A redirect URL must be a full absolute http(s) URL with a dotted hostname
 * (e.g. https://example.com/path) — anything else would resolve relative to
 * the form page or use an unsafe scheme when the visitor is redirected.
 */
export const isValidRedirectUrl = (url: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return false;
  }
  return (
    (parsed.protocol === "http:" || parsed.protocol === "https:") &&
    parsed.hostname.includes(".")
  );
};

// ---- Time-picker disabling (so past times / close ≤ open can't be picked) ----

export interface DateTimeParts {
  /** `YYYY-MM-DD` wall-clock date in the target timezone. */
  date: string;
  /** 24h `HH:mm` wall-clock time in the target timezone. */
  time: string;
  /** Minutes since midnight (0–1439) in the target timezone. */
  minuteOfDay: number;
}

/**
 * Wall-clock parts of `at` in an IANA `timezone`. The schedule's date/time
 * strings are wall-clock values in the schedule's timezone, so "today" and
 * "now" must be derived there — not in the builder user's browser zone. An
 * empty or invalid timezone falls back to the browser zone.
 */
export const getDateTimePartsInTimezone = (
  timezone: string,
  at: Date = new Date()
): DateTimeParts => {
  const buildFormatter = (zone?: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = buildFormatter(timezone || undefined).formatToParts(at);
  } catch {
    parts = buildFormatter().formatToParts(at);
  }
  const get = (type: Intl.DateTimeFormatPart["type"]): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hour = Number(get("hour")) % 24 || 0;
  const minute = Number(get("minute")) || 0;
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    minuteOfDay: hour * 60 + minute,
  };
};

/**
 * Latest invalid minute-of-day (0–1439) for picking a time on `date`, or null
 * when the day is unconstrained. Combines "not in the past" (when `date` is
 * today) with "strictly after the open boundary" (when `minBoundary` falls on
 * the same day). "Today"/"now" are evaluated in the schedule's `timezone`
 * (browser zone when empty) — it guards picking; held values going stale is
 * the validation's job.
 */
export const getTimeFloor = (
  date: string,
  minBoundary: ScheduleBoundary | null = null,
  now: Date = new Date(),
  timezone = ""
): number | null => {
  if (!date) {
    return null;
  }
  const nowParts = getDateTimePartsInTimezone(timezone, now);
  let floor: number | null = null;
  if (date === nowParts.date) {
    floor = nowParts.minuteOfDay;
  }
  if (
    minBoundary?.mode === "date" &&
    minBoundary.date === date &&
    minBoundary.time
  ) {
    const [hour, minute] = minBoundary.time.split(":").map(Number);
    if (Number.isFinite(hour) && Number.isFinite(minute)) {
      floor = Math.max(floor ?? 0, hour * 60 + minute);
    }
  }
  return floor;
};

/** naive-ui `is-hour-disabled`: every minute of the hour is at or below the floor. */
export const isHourDisabledFor = (
  date: string,
  minBoundary: ScheduleBoundary | null,
  hour: number,
  now: Date = new Date(),
  timezone = ""
): boolean => {
  const floor = getTimeFloor(date, minBoundary, now, timezone);
  return floor !== null && hour * 60 + 59 <= floor;
};

/** naive-ui `is-minute-disabled`: the hour+minute lands at or below the floor. */
export const isMinuteDisabledFor = (
  date: string,
  minBoundary: ScheduleBoundary | null,
  minute: number,
  hour: number | null,
  now: Date = new Date(),
  timezone = ""
): boolean => {
  if (hour === null) {
    return false;
  }
  const floor = getTimeFloor(date, minBoundary, now, timezone);
  return floor !== null && hour * 60 + minute <= floor;
};

// ---- Appearance options + helpers (drive the ScheduleAppearance panel + preview) ----

export interface SwatchOption {
  value: string;
  /** i18n key for the option label. */
  labelKey: string;
}

/** Card background swatches (value = hex). */
export const CARD_BACKGROUND_OPTIONS: SwatchOption[] = [
  {
    value: "#ffffff",
    labelKey: "form.scheduleVisibility.appearance.colors.default",
  },
  {
    value: "#f9fafb",
    labelKey: "form.scheduleVisibility.appearance.colors.lightGray",
  },
  {
    value: "#eff4ff",
    labelKey: "form.scheduleVisibility.appearance.colors.brandTint",
  },
  {
    value: "#fef3f2",
    labelKey: "form.scheduleVisibility.appearance.colors.blush",
  },
  {
    value: "#101828",
    labelKey: "form.scheduleVisibility.appearance.colors.dark",
  },
];

/** Page background swatches (value = hex). */
export const PAGE_BACKGROUND_OPTIONS: SwatchOption[] = [
  {
    value: "#f9fafb",
    labelKey: "form.scheduleVisibility.appearance.colors.default",
  },
  {
    value: "#ffffff",
    labelKey: "form.scheduleVisibility.appearance.colors.white",
  },
  {
    value: "#eef1f5",
    labelKey: "form.scheduleVisibility.appearance.colors.coolGray",
  },
  {
    value: "#101828",
    labelKey: "form.scheduleVisibility.appearance.colors.dark",
  },
];

export const SHADOW_CSS: Record<ShadowValue, string> = {
  none: "none",
  soft: "0 1px 3px 0 rgba(16,24,40,0.10), 0 1px 2px 0 rgba(16,24,40,0.06)",
  elevated:
    "0 16px 24px -8px rgba(16,24,40,0.16), 0 4px 8px -4px rgba(16,24,40,0.06)",
};

export const CARD_WIDTH_MIN = 320;
export const CARD_WIDTH_MAX = 720;
export const CARD_HEIGHT_MAX = 900;
export const CORNER_RADIUS_MAX = 32;
export const PADDING_MAX = 120;

const marginCss = (side: MarginSide): string =>
  side === "auto" ? "auto" : `${side}px`;

/** Inline style object for the preview card, mirroring the design's cardStyle(). */
export const getCardStyle = (
  appearance: ScheduleAppearance
): Record<string, string> => {
  const style: Record<string, string> = {
    width: "100%",
    maxWidth: `${appearance.cardWidth}px`,
    background: appearance.cardBackground,
    borderRadius: `${appearance.cornerRadius}px`,
    paddingTop: `${appearance.padding.top}px`,
    paddingRight: `${appearance.padding.right}px`,
    paddingBottom: `${appearance.padding.bottom}px`,
    paddingLeft: `${appearance.padding.left}px`,
    marginTop: marginCss(appearance.margin.top),
    marginRight: marginCss(appearance.margin.right),
    marginBottom: marginCss(appearance.margin.bottom),
    marginLeft: marginCss(appearance.margin.left),
    boxShadow: SHADOW_CSS[appearance.shadow] ?? SHADOW_CSS.soft,
    textAlign: "center",
    boxSizing: "border-box",
  };
  // Like cardWidth (width capped, text wraps), the set height always wins:
  // fixed height with inner scroll, so values below the content height apply.
  if (appearance.cardHeight > 0) {
    style.height = `${appearance.cardHeight}px`;
    style.overflowY = "auto";
  }
  return style;
};

/** Strip the protocol from a URL for compact display. */
export const stripProtocol = (url: string): string =>
  url ? url.replace(/^https?:\/\//i, "").split("/")[0] || url : "";

/**
 * Build the default rich-text HTML for a state's page, matching the design's
 * emoji + title + body structure. Body copy is supplied (translated) by caller.
 */
export const buildDefaultStateHtml = (
  emoji: string,
  title: string,
  body: string
): string =>
  `<div style="font-size:30px;line-height:1;margin-bottom:14px;">${emoji}</div>` +
  `<div style="font-family:Inter,sans-serif;font-size:22px;font-weight:600;color:#101828;margin-bottom:10px;">${title}</div>` +
  `<div style="font-family:Inter,sans-serif;font-size:15px;line-height:24px;color:#475467;">${body}</div>`;

export const DEFAULT_STATE_EMOJI: Record<VisibilityStateKey, string> = {
  before: "\u{1F552}", // 🕒 clock
  after: "\u{1F512}", // 🔒 lock
};
